import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Instagram, MapPin, Phone, Mail, Zap, Users, TrendingUp, Pencil, ChevronDown, ChevronUp, History, Target, Lock, Unlock, Calendar, Camera, Plus, Trash2, Link2, KeyRound, Copy, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AffiliateSnapshot } from "@/components/AffiliateSnapshot";
import { MonthPicker } from "@/components/MonthPicker";
import { ReferrerPicker } from "@/components/ReferrerPicker";
import { ReferralsCard } from "@/components/ReferralsCard";
import { StarterKitBlock } from "@/components/StarterKitBlock";
import { toast } from "sonner";

const STAGES = ["Approved", "Paused"];

export default function AffiliateDetail() {
  const { id } = useParams();
  const [aff, setAff] = useState(null);
  const [orders, setOrders] = useState([]);
  const [visibleOrders, setVisibleOrders] = useState(25);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("mtd");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [comms, setComms] = useState([]);
  const [codes, setCodes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [audit, setAudit] = useState([]);
  const [nextSteps, setNextSteps] = useState(null);
  const [asms, setAsms] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [newCustDialog, setNewCustDialog] = useState(false);
  const [newCustPeriod, setNewCustPeriod] = useState("this_month");
  const [newCustData, setNewCustData] = useState(null);
  const [snapshotMonth, setSnapshotMonth] = useState(""); // YYYY-MM; empty = current IST month
  const { user } = useAuth();
  const canManageTier = user && ["admin", "dept_lead"].includes(user.role);
  const canManagePortal = user && ["admin", "dept_lead", "asm"].includes(user.role);

  // Month label ("Aug 2026") derived from the picker or fallback to current-month key from backend.
  const effectiveMonthKey = snapshotMonth || aff?.current_month_key || "";
  const monthLabel = (() => {
    if (!effectiveMonthKey) return "";
    try {
      const [y, m] = effectiveMonthKey.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleString("en", { month: "short", year: "numeric" });
    } catch { return effectiveMonthKey; }
  })();

  useEffect(() => {
    if (!newCustDialog) return;
    setNewCustData(null);
    api
      .get(`/affiliates/${id}/new-customers`, { params: { period: newCustPeriod } })
      .then((r) => setNewCustData(r.data))
      .catch(() => setNewCustData({ count: 0, customers: [], total_revenue: 0 }));
  }, [newCustDialog, newCustPeriod, id]);

  const [denied, setDenied] = useState("");
  const onLoadErr = (e) => {
    const st = e?.response?.status;
    if (st === 403) setDenied("This affiliate isn't in your cohort.");
    else if (st === 404) setDenied("Affiliate not found.");
  };

  const loadStats = () => {
    const params = { period };
    if (period === "custom") { params.date_from = customFrom; params.date_to = customTo; }
    api.get(`/affiliates/${id}/stats`, { params }).then((r) => setStats(r.data)).catch(onLoadErr);
  };

  const load = () => {
    const monthParams = snapshotMonth ? { month: snapshotMonth } : {};
    api.get(`/affiliates/${id}`, { params: monthParams }).then((r) => setAff(r.data)).catch(onLoadErr);
    api.get(`/orders`, { params: { affiliate_id: id } }).then((r) => setOrders(r.data)).catch(onLoadErr);
    api.get(`/communications`, { params: { affiliate_id: id } }).then((r) => setComms(r.data)).catch(onLoadErr);
    api.get(`/codes`, { params: { affiliate_id: id } }).then((r) => setCodes(r.data)).catch(onLoadErr);
    api.get(`/affiliates/${id}/timeline`).then((r) => setTimeline(r.data)).catch(onLoadErr);
    api.get(`/affiliates/${id}/audit`).then((r) => setAudit(r.data)).catch(onLoadErr);
    api.get(`/affiliates/${id}/next-steps`, { params: monthParams }).then((r) => setNextSteps(r.data)).catch(onLoadErr);
    loadStats();
  };

  useEffect(() => { load(); api.get("/users").then((r) => setAsms(r.data.filter((u) => u.role === "asm"))); }, [id]);
  // Re-fetch month-scoped data (profile + next-steps) whenever the month picker changes
  useEffect(() => {
    if (!id) return;
    const monthParams = snapshotMonth ? { month: snapshotMonth } : {};
    api.get(`/affiliates/${id}`, { params: monthParams }).then((r) => setAff(r.data)).catch(onLoadErr);
    api.get(`/affiliates/${id}/next-steps`, { params: monthParams }).then((r) => setNextSteps(r.data)).catch(onLoadErr);
  }, [snapshotMonth, id]);
  useEffect(() => {
    if (period !== "custom" || (customFrom && customTo)) loadStats();
  }, [period, customFrom, customTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStage = async (newStage) => {
    try {
      await api.patch(`/affiliates/${id}`, { onboarding_stage: newStage });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch { toast.error("Could not update"); }
  };

  if (denied) return <div data-testid="affiliate-access-denied" className="p-10 text-muted-foreground">{denied} <Link to="/affiliates" className="text-primary underline">Back to affiliates</Link></div>;
  if (!aff) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageHeader
        title={aff.name}
        subtitle={monthLabel ? `${aff.niche || "Affiliate"} · ${monthLabel} snapshot` : (aff.niche || "Affiliate")}
        actions={
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <MonthPicker
              value={snapshotMonth}
              onChange={setSnapshotMonth}
              testId="detail-month-picker"
            />
            <Button
              data-testid="open-snapshot-btn"
              onClick={() => setSnapshotOpen(true)}
              size="sm"
              className="rounded-full h-9"
            >
              <Camera className="h-3.5 w-3.5 mr-1.5" /> Snapshot
            </Button>
            <Button data-testid="edit-affiliate-btn" onClick={() => setEditOpen(true)} variant="outline" size="sm" className="rounded-full h-9">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
            <Link to="/affiliates" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5">
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </div>
        }
      />

      <div className="p-6 md:p-10 space-y-6">
        {/* Profile card */}
        <div className="rounded-2xl border border-border/60 bg-card p-6 flex flex-col md:flex-row gap-6">
          {aff.avatar_url ? (
            <img src={aff.avatar_url} alt="" className="h-24 w-24 rounded-2xl object-cover" />
          ) : (
            <div className="h-24 w-24 rounded-2xl bg-secondary flex items-center justify-center text-2xl font-bold">{aff.name.charAt(0)}</div>
          )}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-semibold tracking-tight">{aff.name}</h2>
              <TierBadge tier={aff.tier} />
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground bg-secondary rounded-full px-3 py-1">{aff.onboarding_stage}</span>
              {aff.assigned_asm_name && (
                <span
                  data-testid="affiliate-asm-badge"
                  className="text-xs font-semibold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1 flex items-center gap-1.5"
                  title={aff.assigned_asm_email || ""}
                >
                  <Users className="h-3 w-3" />
                  ASM · {aff.assigned_asm_name}
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              {aff.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{aff.email}</span>}
              {aff.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{aff.phone}</span>}
              {aff.city && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{aff.city}</span>}
              {aff.instagram && <span className="flex items-center gap-1.5"><Instagram className="h-3.5 w-3.5" />{aff.instagram}</span>}
              {aff.followers ? <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{aff.followers.toLocaleString()}</span> : null}
            </div>
            {/* Stage move buttons */}
            <div className="mt-4 flex flex-wrap gap-2">
              {STAGES.map((s) => (
                <button
                  key={s}
                  data-testid={`stage-${s.replace(/\s/g, "-").toLowerCase()}`}
                  onClick={() => changeStage(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${aff.onboarding_stage === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:w-72">
            <div className="rounded-xl bg-primary/10 p-3 col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-primary">Lifetime Sales</div>
              <div className="text-xl font-bold mt-1">{INR(stats?.lifetime?.sales || 0)}</div>
              <div className="text-[10px] text-muted-foreground">{stats?.lifetime?.new_customers || 0} new · {stats?.lifetime?.orders || 0} orders · {INR(stats?.lifetime?.commission || 0)} earned</div>
            </div>
            <div className="rounded-xl bg-secondary/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{snapshotMonth ? `${monthLabel} Sales` : "MTD Sales"}</div>
              <div className="text-lg font-semibold mt-1">{INR(aff.current_month_sales)}</div>
            </div>
            <button
              data-testid="mtd-new-customers-btn"
              onClick={() => { setNewCustPeriod("this_month"); setNewCustDialog(true); }}
              className="rounded-xl bg-secondary/40 p-3 text-left hover:bg-secondary/60 hover:ring-2 hover:ring-primary/30 transition-all cursor-pointer group"
              title="View new customers"
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">{snapshotMonth ? `${monthLabel} New` : "MTD New"}</div>
              <div className="text-lg font-semibold mt-1 flex items-center gap-1">
                {aff.current_month_new_customers}
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
            <div className="rounded-xl bg-secondary/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Commission</div>
              <div className="text-lg font-semibold mt-1 text-primary">{aff.current_month_commission_percent}%</div>
            </div>
            <button
              data-testid="lifetime-new-customers-btn"
              onClick={() => { setNewCustPeriod("lifetime"); setNewCustDialog(true); }}
              className="rounded-xl bg-secondary/40 p-3 text-left hover:bg-secondary/60 hover:ring-2 hover:ring-primary/30 transition-all cursor-pointer group"
              title="View lifetime new customers"
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">Lifetime New</div>
              <div className="text-lg font-semibold mt-1 flex items-center gap-1">
                {aff.lifetime_new_customers}
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
            {stats?.lifetime && (
              <div
                data-testid="repurchase-rate-tile"
                className="rounded-xl bg-secondary/40 p-3 col-span-2"
                title="Share of unique new customers who returned for another purchase"
              >
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Repurchase Rate</div>
                <div className="text-lg font-semibold mt-1 flex items-baseline gap-2">
                  <span className={stats.lifetime.repurchase_rate > 50 ? "text-emerald-500" : stats.lifetime.repurchase_rate > 25 ? "text-primary" : "text-rose-500"}>
                    {stats.lifetime.repurchase_rate}%
                  </span>
                  <span className="text-[10px] font-normal text-muted-foreground">
                    {stats.lifetime.repeat_customers} of {stats.lifetime.unique_new_customers} came back
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Period stats */}
        {stats && (
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <h3 className="text-lg font-semibold tracking-tight">Performance</h3>
              <span className="text-xs text-muted-foreground">{stats.label}</span>
              <div className="ml-auto flex gap-2 items-center">
                <select data-testid="period-select" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 rounded-full border border-border bg-card px-3 text-xs font-medium">
                  <option value="mtd">Month to Date</option>
                  <option value="last_30">Last 30 Days</option>
                  <option value="last_90">Last 90 Days</option>
                  <option value="qtd">Quarter to Date</option>
                  <option value="ytd">Year to Date</option>
                  <option value="all_time">All Time</option>
                  <option value="custom">Custom Range</option>
                </select>
                {period === "custom" && (
                  <>
                    <input data-testid="period-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 rounded-full border border-border bg-card px-3 text-xs" />
                    <span className="text-xs text-muted-foreground">to</span>
                    <input data-testid="period-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 rounded-full border border-border bg-card px-3 text-xs" />
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <PeriodStat label="Sales" value={INR(stats.period_stats.sales)} testId="period-sales" />
              <PeriodStat label="New Customers" value={stats.period_stats.new_customers} testId="period-new" />
              <PeriodStat label="Orders" value={stats.period_stats.orders} testId="period-orders" />
              <PeriodStat label="Commission" value={INR(stats.period_stats.commission)} testId="period-commission" />
              <PeriodStat label="Avg Order" value={INR(stats.period_stats.avg_order_value)} testId="period-aov" />
            </div>
          </div>
        )}

        {/* Next Steps — tier progression guidance */}
        {nextSteps && <NextStepsCard data={nextSteps} affId={id} canManageTier={canManageTier} onChange={load} kit={aff.starter_kit} canEditKit={canManagePortal} />}

        {/* Referral program — who this affiliate referred + reward status */}
        <ReferralsCard affiliateId={id} />

        {/* Call History timeline */}
        {(aff.launch_call_at || aff.onboarding_call_at || aff.pro_tier_growth_call_at || aff.pro_advance_tier_growth_call_at || aff.legend_growth_call_at) && (
          <div data-testid="call-history-card" className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold tracking-tight">Call History</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { k: "launch_call_at", label: "Launch" },
                { k: "onboarding_call_at", label: "Onboarding" },
                { k: "pro_tier_growth_call_at", label: "Pro-Tier Growth" },
                { k: "pro_advance_tier_growth_call_at", label: "Pro Adv Growth" },
                { k: "legend_growth_call_at", label: "Legend Growth" },
              ].map(({ k, label }) => (
                <div key={k} data-testid={`call-${k}`} className={`rounded-xl border p-3 ${aff[k] ? "border-primary/30 bg-primary/5" : "border-border/60 bg-secondary/20 opacity-60"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                  <div className="text-sm font-semibold mt-1">
                    {aff[k] ? new Date(aff[k]).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fast track card */}
        {aff.onboarding_call_at && (
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold tracking-tight">Fast Track Bonus</h3>
              <span className={`text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 ${
                aff.fast_track_status === "achieved" ? "bg-emerald-500/10 text-emerald-600" :
                aff.fast_track_status === "missed" ? "bg-rose-500/10 text-rose-600" :
                "bg-primary/10 text-primary"
              }`}>{aff.fast_track_status?.replace("_", " ")}</span>
            </div>
            <p className="text-sm text-muted-foreground">2 new customers in 30 days of onboarding call → ₹2,000 MRP bonus</p>
            {nextSteps?.fast_track && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {nextSteps.fast_track.new_customers_so_far} / {nextSteps.fast_track.target_new_customers} new customers <span className="text-muted-foreground font-normal">in window</span>
                  </span>
                  {nextSteps.fast_track.status === "pending" && (
                    <span className="text-muted-foreground">{nextSteps.fast_track.days_remaining} days remaining</span>
                  )}
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      nextSteps.fast_track.status === "achieved" ? "bg-emerald-500" :
                      nextSteps.fast_track.status === "missed" ? "bg-rose-500" :
                      "bg-primary"
                    }`}
                    style={{ width: `${Math.min(100, (nextSteps.fast_track.new_customers_so_far / nextSteps.fast_track.target_new_customers) * 100)}%` }}
                  />
                </div>
                {(nextSteps.fast_track.anchor || nextSteps.fast_track.deadline) && (
                  <div className="text-[11px] text-muted-foreground pt-1" data-testid="fast-track-window">
                    Window:{" "}
                    <span className="font-medium">
                      {new Date(nextSteps.fast_track.anchor).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>{" "}→{" "}
                    <span className="font-medium">
                      {new Date(nextSteps.fast_track.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Portal access card */}
        {canManagePortal && <PortalAccessCard aff={aff} />}

        {/* Tabs */}
        <Tabs defaultValue="orders">
          <TabsList className="rounded-full bg-secondary p-1 h-11">
            <TabsTrigger data-testid="tab-orders" value="orders" className="rounded-full px-4">Orders</TabsTrigger>
            <TabsTrigger data-testid="tab-codes" value="codes" className="rounded-full px-4">Codes</TabsTrigger>
            <TabsTrigger data-testid="tab-comms" value="comms" className="rounded-full px-4">Communications</TabsTrigger>
            <TabsTrigger data-testid="tab-timeline" value="timeline" className="rounded-full px-4">Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto mt-4">
              <div className="flex items-center justify-between px-5 py-3 bg-secondary/40 border-b border-border/50">
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Showing {Math.min(visibleOrders, orders.length)} of {orders.length} orders
                </div>
                {orders.length > 25 && (
                  <div className="flex gap-2">
                    <select data-testid="orders-page-size" value={visibleOrders} onChange={(e) => setVisibleOrders(parseInt(e.target.value, 10))} className="h-8 rounded-full border border-border bg-card px-3 text-xs">
                      <option value={25}>Show 25</option>
                      <option value={50}>Show 50</option>
                      <option value={100}>Show 100</option>
                      <option value={99999}>Show all</option>
                    </select>
                  </div>
                )}
              </div>
              <table className="w-full">
                <thead className="bg-secondary/20">
                  <tr>
                    <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3">Order</th>
                    <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3">Date</th>
                    <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3">Customer</th>
                    <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3">Code</th>
                    <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3">Amount</th>
                    <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.slice(0, visibleOrders).map((o) => (
                    <tr key={o.id} className="border-t border-border/50">
                      <td className="px-5 py-3 text-sm font-mono">{o.order_ref}</td>
                      <td className="px-5 py-3 text-sm text-muted-foreground">{o.order_date?.slice(0, 10)}</td>
                      <td className="px-5 py-3 text-sm">
                        {o.customer_email}
                        {o.is_new_customer && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold uppercase">New</span>}
                      </td>
                      <td className="px-5 py-3 text-xs font-mono">
                        {o.discount_code || (o.attribution_source === "tag" && o.attribution_tag ? (
                          <span
                            data-testid={`aff-order-tag-${o.id}`}
                            className="inline-block px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide text-[10px]"
                            title={`Attributed via Shopify tag: ${o.attribution_tag}`}
                          >
                            Tag · {o.attribution_tag}
                          </span>
                        ) : "—")}
                      </td>
                      <td className="px-5 py-3 text-sm font-medium text-right">{INR(o.amount)}</td>
                      <td className="px-5 py-3 text-sm text-right">{INR(o.commission_amount)} <span className="text-xs text-muted-foreground">({o.commission_percent}%)</span></td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-muted-foreground">No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
              {orders.length > visibleOrders && (
                <div className="border-t border-border/50 p-3 flex justify-center">
                  <button data-testid="orders-load-more" onClick={() => setVisibleOrders((v) => v + 25)} className="text-sm text-primary font-semibold hover:underline">
                    Load 25 more
                  </button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="codes">
            <div className="rounded-2xl border border-border/60 bg-card p-6 mt-4 space-y-3">
              {codes.length === 0 && <div className="text-sm text-muted-foreground">No codes assigned.</div>}
              {codes.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-xl border border-border/60 p-4">
                  <div>
                    <div className="text-lg font-mono font-bold">{c.code}</div>
                    <div className="text-xs text-muted-foreground mt-1">{c.referral_link} · {c.usage_count} uses · {c.discount_percent}% off</div>
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 ${c.active ? "bg-emerald-500/10 text-emerald-600" : "bg-neutral-500/10 text-neutral-600"}`}>{c.active ? "Active" : "Inactive"}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="comms">
            <CommsPanel affiliateId={id} comms={comms} onAdded={load} />
          </TabsContent>

          <TabsContent value="timeline">
            <div className="rounded-2xl border border-border/60 bg-card p-6 mt-4 space-y-3">
              {timeline.length === 0 && <div className="text-sm text-muted-foreground">No events yet.</div>}
              {timeline.map((t) => (
                <div key={t.id} className="flex gap-4">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t.from_tier ? `${t.from_tier} → ` : ""}{t.to_tier}</div>
                    <div className="text-xs text-muted-foreground">{t.reason} · {new Date(t.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Unobtrusive change history */}
        <div className="rounded-2xl border border-border/60 bg-card/60">
          <button
            data-testid="toggle-history"
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-secondary/40 transition-colors rounded-2xl"
          >
            <div className="flex items-center gap-2 text-sm">
              <History className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Change History</span>
              <span className="text-xs text-muted-foreground">{audit.length} edit{audit.length === 1 ? "" : "s"}</span>
            </div>
            {historyOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
          {historyOpen && (
            <div data-testid="audit-log" className="px-5 pb-5 pt-1 space-y-3 border-t border-border/50">
              {audit.length === 0 && <div className="text-xs text-muted-foreground pt-3">No manual edits yet.</div>}
              {audit.map((a) => (
                <div key={a.id} className="text-xs">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-semibold">{a.user_name || a.actor_email || "System"}</span>
                    {a.user_role && <span className="text-muted-foreground">({a.user_role.replace("_", " ")})</span>}
                    <span className="text-muted-foreground ml-auto">{new Date(a.at).toLocaleString()}</span>
                  </div>
                  <ul className="ml-0 space-y-0.5">
                    {a.action && !a.changes && <li className="text-muted-foreground">{a.action.replace(/_/g, " ")}{a.target_email ? ` · ${a.target_email}` : ""}</li>}
                    {(a.changes || []).map((ch, i) => (
                      <li key={i} className="text-muted-foreground">
                        <span className="font-medium text-foreground">{ch.field.replace(/_/g, " ")}</span>:{" "}
                        <span className="line-through opacity-60">{formatVal(ch.from)}</span>{" → "}
                        <span className="text-foreground">{formatVal(ch.to)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <EditAffiliateDialog open={editOpen} onOpenChange={setEditOpen} aff={aff} asms={asms} onSaved={() => { setEditOpen(false); load(); }} />
      <AffiliateSnapshot
        open={snapshotOpen}
        onOpenChange={setSnapshotOpen}
        affiliate={aff}
        nextSteps={nextSteps}
        monthKey={effectiveMonthKey}
      />
      <NewCustomersDialog
        open={newCustDialog}
        onOpenChange={setNewCustDialog}
        period={newCustPeriod}
        onPeriodChange={setNewCustPeriod}
        data={newCustData}
        affiliateName={aff.name}
        onOrderMarked={() => {
          // Refresh both dialog and full affiliate stats
          load();
          api
            .get(`/affiliates/${id}/new-customers`, { params: { period: newCustPeriod } })
            .then((r) => setNewCustData(r.data));
        }}
      />
    </div>
  );
}

function NewCustomersDialog({ open, onOpenChange, period, onPeriodChange, data, affiliateName, onOrderMarked }) {
  const { user } = useAuth();
  const canEdit = user && ["admin", "dept_lead"].includes(user.role);
  const [busy, setBusy] = useState("");

  const markRepeat = async (orderId, email) => {
    if (!window.confirm(`Mark ${email} as a repeat customer (not new)?`)) return;
    setBusy(orderId);
    try {
      await api.post(`/admin/orders/${orderId}/mark-repeat`);
      toast.success("Marked as repeat customer");
      onOrderMarked && onOrderMarked();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-customers-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Users className="h-4 w-4" /> New Customers
            <span className="text-sm text-muted-foreground font-normal">· {affiliateName}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          <button
            data-testid="new-cust-period-this_month"
            onClick={() => onPeriodChange("this_month")}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
              period === "this_month"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/60 hover:bg-secondary"
            }`}
          >
            This Month
          </button>
          <button
            data-testid="new-cust-period-lifetime"
            onClick={() => onPeriodChange("lifetime")}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
              period === "lifetime"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/60 hover:bg-secondary"
            }`}
          >
            Lifetime
          </button>
          {data && (
            <span className="ml-auto text-xs text-muted-foreground">
              {data.count} new customer{data.count !== 1 ? "s" : ""} · {INR(data.total_revenue)} revenue
            </span>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto rounded-xl border border-border/60">
          {!data ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>
          ) : data.count === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No new customers in this period yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Order Date</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">Order</th>
                  {canEdit && <th className="px-4 py-2"></th>}
                </tr>
              </thead>
              <tbody data-testid="new-customers-list">
                {data.customers.map((c) => (
                  <tr key={c.id} className="border-t border-border/40 hover:bg-secondary/20">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-medium">{c.customer_name || "—"}</div>
                          <div className="text-xs text-muted-foreground">{c.customer_email}</div>
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold uppercase">New</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{c.order_date?.slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{INR(c.amount)}</td>
                    <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{c.order_ref}</td>
                    {canEdit && (
                      <td className="px-4 py-2.5 text-right">
                        <button
                          data-testid={`mark-repeat-${c.id}`}
                          disabled={busy === c.id}
                          onClick={() => markRepeat(c.id, c.customer_email)}
                          className="text-[10px] text-muted-foreground hover:text-amber-600 underline-offset-2 hover:underline transition-colors disabled:opacity-40"
                          title="Actually a repeat customer — flip is_new_customer=false"
                        >
                          Mark as repeat
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PeriodStat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl bg-secondary/40 p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && v.length > 40) return v.slice(0, 40) + "…";
  if (typeof v === "object") {
    if ("purchased" in v) return v.purchased ? ["Yes", v.purchased_on, v.invoice_number, v.invoice_amount != null ? `₹${v.invoice_amount}` : null].filter(Boolean).join(" · ") : "No";
    return JSON.stringify(v);
  }
  return String(v);
}

function EditAffiliateDialog({ open, onOpenChange, aff, asms, onSaved }) {
  const { user } = useAuth();
  const canManageTier = user && ["admin", "dept_lead"].includes(user.role);
  const EDITABLE = ["name", "email", "phone", "city", "instagram", "youtube", "followers", "niche", "avatar_url", "assigned_asm_id", "notes", "onboarding_call_at", "launch_call_at", "pro_tier_growth_call_at", "pro_advance_tier_growth_call_at", "legend_growth_call_at", "referred_by_affiliate_id"];
  const initial = EDITABLE.reduce((acc, k) => { acc[k] = aff[k] ?? ""; return acc; }, {});
  const [form, setForm] = useState(initial);
  const [newTier, setNewTier] = useState(aff.tier);
  const [tierReason, setTierReason] = useState("");
  // Codes state — each row: { id (uuid or "new-N"), code, referral_link, discount_percent, active, _new?, _deleted? }
  const [codes, setCodes] = useState([]);
  const [codesLoaded, setCodesLoaded] = useState(false);

  useEffect(() => {
    setForm(EDITABLE.reduce((acc, k) => { acc[k] = aff[k] ?? ""; return acc; }, {}));
    setNewTier(aff.tier);
  }, [aff]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) { setCodesLoaded(false); return; }
    api.get("/codes", { params: { affiliate_id: aff.id } }).then((r) => {
      setCodes(r.data.map((c) => ({ ...c, _pristine: c })));
      setCodesLoaded(true);
    }).catch(() => {
      setCodes([]);
      setCodesLoaded(true);
    });
  }, [open, aff.id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateCodeRow = (idx, patch) => {
    setCodes((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const addCodeRow = () => {
    setCodes((prev) => [...prev, {
      id: `new-${Date.now()}`,
      code: "",
      referral_link: "",
      _new: true,
    }]);
  };
  const removeCodeRow = (idx) => {
    setCodes((prev) => prev.map((c, i) => (
      i !== idx ? c : (c._new ? null : { ...c, _deleted: true })
    )).filter(Boolean));
  };
  const undoRemove = (idx) => updateCodeRow(idx, { _deleted: false });

  const persistCodes = async () => {
    for (const c of codes) {
      if (c._deleted && !c._new) {
        await api.delete(`/codes/${c.id}`);
      } else if (c._new && !c._deleted) {
        if (!c.code?.trim()) continue;
        await api.post("/codes", {
          affiliate_id: aff.id,
          code: c.code.trim().toUpperCase(),
          referral_link: (c.referral_link || "").trim() || null,
        });
      } else if (!c._new && !c._deleted && c._pristine) {
        const p = c._pristine;
        const patch = {};
        const codeNorm = (c.code || "").trim().toUpperCase();
        if (codeNorm && codeNorm !== p.code) patch.code = codeNorm;
        const linkNorm = (c.referral_link || "").trim();
        if (linkNorm !== (p.referral_link || "")) patch.referral_link = linkNorm || null;
        if (Object.keys(patch).length > 0) {
          await api.patch(`/codes/${c.id}`, patch);
        }
      }
    }
  };

  const save = async () => {
    const patch = {};
    for (const k of EDITABLE) {
      const orig = aff[k] ?? "";
      const next = form[k] ?? "";
      if (String(orig) !== String(next)) {
        if (k === "followers") patch[k] = parseInt(next || 0, 10);
        else if (["onboarding_call_at", "launch_call_at", "pro_tier_growth_call_at", "pro_advance_tier_growth_call_at", "legend_growth_call_at"].includes(k)) patch[k] = next ? new Date(next).toISOString() : null;
        else patch[k] = next === "" ? null : next;
      }
    }
    try {
      if (Object.keys(patch).length > 0) {
        await api.patch(`/affiliates/${aff.id}`, patch);
      }
      if (canManageTier && newTier !== aff.tier) {
        await api.post(`/affiliates/${aff.id}/tier`, null, { params: { new_tier: newTier, reason: tierReason || undefined } });
      }
      await persistCodes();
      toast.success("Affiliate updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const dateForInput = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toISOString().slice(0, 16); } catch { return ""; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg">
        <DialogHeader><DialogTitle>Edit Affiliate</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="col-span-2"><Label>Name</Label><Input data-testid="edit-name" value={form.name} onChange={set("name")} className="mt-1 rounded-xl" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1 rounded-xl" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1 rounded-xl" /></div>
          <div><Label>City</Label><Input value={form.city} onChange={set("city")} className="mt-1 rounded-xl" /></div>
          <div><Label>Niche</Label><Input value={form.niche} onChange={set("niche")} className="mt-1 rounded-xl" /></div>
          <div><Label>Instagram</Label><Input value={form.instagram} onChange={set("instagram")} className="mt-1 rounded-xl" /></div>
          <div><Label>YouTube</Label><Input value={form.youtube} onChange={set("youtube")} className="mt-1 rounded-xl" /></div>
          <div><Label>Followers</Label><Input type="number" value={form.followers} onChange={set("followers")} className="mt-1 rounded-xl" /></div>
          <div><Label>Avatar URL</Label><Input value={form.avatar_url} onChange={set("avatar_url")} className="mt-1 rounded-xl" /></div>
          <div className="col-span-2"><Label>Assigned ASM</Label>
            <select value={form.assigned_asm_id || ""} onChange={set("assigned_asm_id")} className="mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm">
              <option value="">— None —</option>
              {asms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Launch Call</Label>
            <Input data-testid="edit-launch-call" type="datetime-local" value={dateForInput(form.launch_call_at)} onChange={(e) => setForm({ ...form, launch_call_at: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Onboarding Call <span className="text-[10px] text-muted-foreground">(arms Fast Track)</span></Label>
            <Input data-testid="edit-onboarding-call" type="datetime-local" value={dateForInput(form.onboarding_call_at)} onChange={(e) => setForm({ ...form, onboarding_call_at: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Pro-Tier Growth Call</Label>
            <Input data-testid="edit-pro-growth-call" type="datetime-local" value={dateForInput(form.pro_tier_growth_call_at)} onChange={(e) => setForm({ ...form, pro_tier_growth_call_at: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Pro Advanced Growth Call</Label>
            <Input data-testid="edit-pro-adv-growth-call" type="datetime-local" value={dateForInput(form.pro_advance_tier_growth_call_at)} onChange={(e) => setForm({ ...form, pro_advance_tier_growth_call_at: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div className="col-span-2">
            <Label>Legend Growth Call</Label>
            <Input data-testid="edit-legend-growth-call" type="datetime-local" value={dateForInput(form.legend_growth_call_at)} onChange={(e) => setForm({ ...form, legend_growth_call_at: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          {canManageTier && (
            <div className="col-span-2">
              <Label className="flex items-center gap-1.5">
                Referred by
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">— Admin / Dept Lead only</span>
              </Label>
              <ReferrerPicker
                value={form.referred_by_affiliate_id || ""}
                onChange={(v) => setForm({ ...form, referred_by_affiliate_id: v })}
                excludeId={aff.id}
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                One-time ₹7,500 stack for the referrer when this affiliate first reaches Pro. Retroactive rewards are not issued.
              </div>
            </div>
          )}
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 rounded-xl" /></div>

          {/* Discount Codes & Affiliate Links */}
          <div className="col-span-2 mt-2 pt-3 border-t border-border/50">
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Discount Codes &amp; Affiliate Links</Label>
              <button
                type="button"
                data-testid="add-code-row"
                onClick={addCodeRow}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add code
              </button>
            </div>
            {!codesLoaded && <div className="text-xs text-muted-foreground">Loading codes…</div>}
            {codesLoaded && codes.length === 0 && (
              <div className="text-xs text-muted-foreground italic">No codes yet. Click &quot;Add code&quot; to give this affiliate a discount code + tracking link.</div>
            )}
            <div className="space-y-2">
              {codes.map((c, i) => (
                <div key={c.id} data-testid={`code-row-${i}`} className={`rounded-xl border ${c._deleted ? "border-red-500/30 bg-red-500/5" : "border-border/60 bg-card/50"} p-3 space-y-2`}>
                  {c._deleted ? (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-red-600 font-mono">{c.code} · will be deleted</span>
                      <button type="button" onClick={() => undoRemove(i)} className="text-xs text-primary hover:underline">Undo</button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-11">
                          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Code</Label>
                          <Input
                            data-testid={`code-${i}-code`}
                            value={c.code}
                            onChange={(e) => updateCodeRow(i, { code: e.target.value })}
                            placeholder="RIYA16"
                            className="mt-1 rounded-lg uppercase h-9 font-mono text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <button
                            type="button"
                            data-testid={`code-${i}-delete`}
                            onClick={() => removeCodeRow(i)}
                            className="w-9 h-9 rounded-lg border border-border/60 hover:border-red-500/30 hover:bg-red-500/5 hover:text-red-600 text-muted-foreground flex items-center justify-center transition-colors"
                            title="Remove this code"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Referral Link</Label>
                        <Input
                          data-testid={`code-${i}-link`}
                          value={c.referral_link || ""}
                          onChange={(e) => updateCodeRow(i, { referral_link: e.target.value })}
                          placeholder="https://fuelled.in/?ref=RIYA16"
                          className="mt-1 rounded-lg h-9 text-xs"
                        />
                      </div>
                      {!c._new && c.usage_count !== undefined && (
                        <div className="text-[10px] text-muted-foreground">Used {c.usage_count} time{c.usage_count === 1 ? "" : "s"}</div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {canManageTier && (
            <>
              <div className="col-span-2 mt-2 pt-3 border-t border-border/50">
                <Label className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Tier Override <span className="text-xs text-muted-foreground">(admin only)</span></Label>
                <select data-testid="edit-tier" value={newTier} onChange={(e) => setNewTier(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm">
                  <option value="Base">Base</option>
                  <option value="Pro">Pro</option>
                  <option value="Pro Advanced">Pro Advanced</option>
                  <option value="Legend">Legend</option>
                </select>
                {newTier !== aff.tier && (
                  <>
                    <Input value={tierReason} onChange={(e) => setTierReason(e.target.value)} placeholder="Reason for override (optional)" className="mt-2 rounded-xl" />
                    <div className="text-xs text-amber-600 mt-1">⚠ Override will lock the tier — auto-recompute won&apos;t change it until unlocked.</div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid="save-edit" onClick={save} className="rounded-full">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NextStepsCard({ data, affId, canManageTier, onChange, kit, canEditKit }) {
  const unlock = async () => {
    try {
      await api.post(`/affiliates/${affId}/tier/unlock`);
      toast.success("Tier unlocked — will auto-recompute");
      onChange();
    } catch (e) { toast.error("Could not unlock"); }
  };

  const cm = data.current_month;
  const cw = data.current_window;
  const comm = data.commission_this_month;

  // Progress to next reward slab
  const slabPct = cm.next_slab
    ? Math.min(100, (cm.sales / cm.next_slab.sales_needed) * 100)
    : 100;

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold tracking-tight">Next Steps</h3>
        {data.tier_locked && (
          <span className="text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 bg-amber-500/10 text-amber-600 flex items-center gap-1">
            <Lock className="h-3 w-3" /> Tier Locked
          </span>
        )}
        {data.tier_locked && canManageTier && (
          <button data-testid="unlock-tier-btn" onClick={unlock} className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <Unlock className="h-3 w-3" /> Unlock
          </button>
        )}
      </div>

      <StarterKitBlock affId={affId} kit={kit} canEdit={canEditKit} onSaved={onChange} />

      {/* Current-month slab + commission slab (side-by-side) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div data-testid="slab-card" className="rounded-xl border border-border/60 bg-secondary/20 p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Reward Slab Unlocked ({cm.month})</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-bold">₹{cm.reward_slab_unlocked_mrp.toLocaleString("en-IN")}</div>
            <div className="text-xs text-muted-foreground">free products @ MRP</div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${slabPct}%` }} />
          </div>
          <div className="mt-2 text-xs">
            {cm.next_slab ? (
              <span>
                <span className="font-medium">₹{Math.round(cm.next_slab.gap).toLocaleString("en-IN")}</span>
                <span className="text-muted-foreground"> more sales to unlock </span>
                <span className="font-medium">₹{cm.next_slab.reward_mrp.toLocaleString("en-IN")}</span>
                <span className="text-muted-foreground"> slab</span>
              </span>
            ) : (
              <span className="text-emerald-600 font-medium">Top slab unlocked this month 🎉</span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">Current month sales: ₹{Math.round(cm.sales).toLocaleString("en-IN")}</div>
        </div>

        <div data-testid="commission-card" className="rounded-xl border border-border/60 bg-secondary/20 p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Commission % This Month</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-bold">{comm.current_percent}%</div>
            <div className="text-xs text-muted-foreground">on {comm.new_customers_this_month} new customer{comm.new_customers_this_month !== 1 ? "s" : ""}</div>
          </div>
          <div className="mt-3 text-xs">
            {comm.next_step ? (
              <span>
                <span className="font-medium">{comm.next_step.new_customers_needed}</span>
                <span className="text-muted-foreground"> more new customer{comm.next_step.new_customers_needed !== 1 ? "s" : ""} to hit </span>
                <span className="font-medium">{comm.next_step.next_percent}%</span>
              </span>
            ) : (
              <span className="text-emerald-600 font-medium">Top commission slab hit for the month</span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">Current window ({cw.label}): {cw.new_customers} new · ₹{Math.round(cw.revenue).toLocaleString("en-IN")}</div>
        </div>
      </div>

      {/* Tier progression steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {data.steps.map((s, i) => (
          <div key={i} data-testid={`step-${s.target.replace(/\s/g, "-").toLowerCase()}`} className={`rounded-xl border p-4 ${s.done ? "border-emerald-500/40 bg-emerald-500/5" : "border-border/60"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">→ {s.target}</div>
              {s.done && <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600">Reached</span>}
            </div>
            {s.unlock_reward_mrp > 0 && (
              <div
                data-testid={`tier-unlock-reward-${s.target.replace(/\s/g, "-").toLowerCase()}`}
                className="mb-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest bg-primary/10 text-primary rounded-full px-2 py-0.5"
              >
                <Zap className="h-3 w-3" /> ₹{s.unlock_reward_mrp.toLocaleString("en-IN")} MRP unlock bonus
              </div>
            )}
            <div className="text-xs text-muted-foreground mb-2">{s.requirement}</div>
            <div className="text-xs font-medium">{s.progress}</div>
            {!s.done && <div className="text-xs text-primary mt-2">{s.remaining}</div>}
            {s.best_window_label && !s.done && (
              <div className="text-[11px] text-muted-foreground mt-2 border-t border-border/60 pt-2">
                Best window ever ({s.best_window_label}): {s.best_window}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CommsPanel({ affiliateId, comms, onAdded }) {
  const [type, setType] = useState("note");
  const [body, setBody] = useState("");
  const add = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/communications", { affiliate_id: affiliateId, type, body });
      setBody("");
      onAdded();
      toast.success("Logged");
    } catch { toast.error("Could not log"); }
  };
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 mt-4 space-y-4">
      <div className="flex gap-3">
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium">
          <option value="note">Note</option>
          <option value="call">Call</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <Textarea data-testid="comm-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What happened?" className="rounded-xl flex-1" rows={2} />
        <Button data-testid="log-comm-btn" onClick={add} className="rounded-full">Log</Button>
      </div>
      <div className="space-y-2">
        {comms.map((c) => <CommItem key={c.id} c={c} onChanged={onAdded} />)}
        {comms.length === 0 && <div className="text-sm text-muted-foreground">No communications logged.</div>}
      </div>
    </div>
  );
}

function CommItem({ c, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(c.body || "");
  const [editType, setEditType] = useState(c.type || "note");
  const [showThread, setShowThread] = useState(false);
  const [comments, setComments] = useState(null);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);

  const saveEdit = async () => {
    try {
      await api.patch(`/communications/${c.id}`, { body: editBody.trim(), type: editType });
      toast.success("Updated");
      setEditing(false);
      onChanged();
    } catch { toast.error("Could not update"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this communication and its comments?")) return;
    try {
      await api.delete(`/communications/${c.id}`);
      toast.success("Deleted");
      onChanged();
    } catch { toast.error("Could not delete"); }
  };

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/communications/${c.id}/comments`);
      setComments(data);
    } catch { setComments([]); }
  };

  const toggleThread = async () => {
    const next = !showThread;
    setShowThread(next);
    if (next && comments === null) await loadComments();
  };

  const submitReply = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/communications/${c.id}/comments`, { body: reply.trim() });
      setComments((prev) => [...(prev || []), data]);
      setReply("");
    } catch { toast.error("Could not post"); }
    setPosting(false);
  };

  return (
    <div data-testid={`comm-item-${c.id}`} className="rounded-xl border border-border/60 p-4">
      <div className="flex items-center justify-between mb-1 gap-3">
        {editing ? (
          <select value={editType} onChange={(e) => setEditType(e.target.value)} className="h-7 rounded-full border border-border bg-card px-3 text-[11px] font-semibold uppercase tracking-widest">
            <option value="note">Note</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        ) : (
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">{c.type}</span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
          {!editing && (
            <>
              <button data-testid={`comm-edit-${c.id}`} onClick={() => setEditing(true)} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors font-medium">Edit</button>
              <button data-testid={`comm-delete-${c.id}`} onClick={del} className="text-[11px] text-muted-foreground hover:text-rose-500 transition-colors font-medium">Delete</button>
            </>
          )}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2} className="rounded-xl" />
          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdit} disabled={!editBody.trim()} className="rounded-full">Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditBody(c.body || ""); setEditType(c.type); }} className="rounded-full">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="text-sm whitespace-pre-wrap">{c.body}</div>
      )}
      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span>by {c.by_user_name}</span>
        <button data-testid={`comm-thread-${c.id}`} onClick={toggleThread} className="font-medium hover:text-foreground transition-colors">
          {showThread ? "Hide comments" : "Comment"}
        </button>
      </div>
      {showThread && (
        <div className="mt-3 pl-3 border-l-2 border-border/50 space-y-3">
          {comments === null ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No comments yet.</div>
          ) : (
            comments.map((cm) => (
              <div key={cm.id} className="text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold">{cm.by_user_name}</span>
                  <span className="text-[10px] text-muted-foreground">{new Date(cm.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap break-words">{cm.body}</div>
              </div>
            ))
          )}
          <div className="flex gap-2 pt-1">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitReply(); }}
              placeholder="Add a comment…"
              rows={2}
              className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <Button size="sm" onClick={submitReply} disabled={posting || !reply.trim()} className="rounded-full">Post</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PortalAccessCard({ aff }) {
  const [status, setStatus] = useState(null);
  const [issuedOtp, setIssuedOtp] = useState("");  // one-time password from invite/regen; local-only
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/affiliate/admin/portal-status/${aff.id}`)
      .then((r) => setStatus(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load portal status"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [aff.id]);

  const invite = async (isRegenerate) => {
    if (isRegenerate && !window.confirm("Regenerate a fresh password? The current one will stop working immediately and the affiliate will be signed out on every device.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/affiliate/admin/invite", { affiliate_id: aff.id });
      // Hold the OTP in local state ONLY. It's shown exactly once; after reload
      // it's gone forever and the admin must regenerate to see another one.
      // (SEC-001 — passwords are no longer persisted or readable from the DB.)
      setIssuedOtp(data?.one_time_password || "");
      toast.success(isRegenerate ? "New password generated — share it now, it won't be shown again" : "Portal invite created — share the password now");
      setReveal(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };
  const copyBoth = () => {
    const both = `Fuelled Portal login\nURL: ${window.location.origin}/affiliate/login\nEmail: ${status.email}\nPassword: ${issuedOtp}`;
    navigator.clipboard.writeText(both).then(() => toast.success("Login details copied"));
  };

  return (
    <div data-testid="portal-access-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold tracking-tight">Portal Access</h3>
          {status?.invited && (
            <span className="text-[10px] font-semibold uppercase tracking-widest rounded-full px-2.5 py-0.5 bg-emerald-500/10 text-emerald-600">Invited</span>
          )}
          {status && !status.invited && (
            <span className="text-[10px] font-semibold uppercase tracking-widest rounded-full px-2.5 py-0.5 bg-muted text-muted-foreground">Not invited</span>
          )}
        </div>
        {status?.invited && (
          <a
            href="/affiliate/login"
            target="_blank"
            rel="noreferrer"
            data-testid="portal-login-link"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            Open login page ↗
          </a>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground italic">Loading…</div>
      ) : !status.invited ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Grant <span className="font-semibold text-foreground">{aff.name}</span> access to their self-serve dashboard at <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">/affiliate/login</span> — a one-time password will be generated for you to share with them.
          </p>
          <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-background px-4 py-3">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Login email</div>
              <div data-testid="portal-affiliate-email" className="text-sm font-mono truncate">{status.affiliate_email || <span className="italic text-muted-foreground">No email on affiliate — add one via Edit first</span>}</div>
            </div>
          </div>
          <Button
            data-testid="portal-invite-btn"
            disabled={busy || !status.affiliate_email}
            onClick={() => invite(false)}
            className="rounded-full"
          >
            <ShieldCheck className="h-4 w-4 mr-1.5" /> {busy ? "Inviting…" : "Invite to Portal"}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Email row */}
          <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-background px-4 py-3">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</div>
              <div data-testid="portal-email-value" className="text-sm font-mono truncate">{status.email}</div>
            </div>
            <button
              data-testid="portal-copy-email"
              onClick={() => copy(status.email, "Email")}
              className="h-8 w-8 rounded-full bg-secondary hover:bg-secondary/70 flex items-center justify-center shrink-0"
              title="Copy email"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Password row — shown ONLY immediately after invite / regen.
              Passwords are never persisted (SEC-001). If the admin reloads
              the page without regenerating, this field will show the
              "security hidden" state. */}
          <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-background px-4 py-3">
            <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Password</div>
              {issuedOtp ? (
                <>
                  <div data-testid="portal-password-value" className="text-sm font-mono truncate">
                    {reveal ? issuedOtp : "•".repeat(Math.max(8, issuedOtp.length))}
                  </div>
                  <div className="text-[10px] text-amber-600 mt-0.5">
                    Shown once · copy &amp; share now · never retrievable from the database
                  </div>
                </>
              ) : (
                <div className="text-xs italic text-muted-foreground">
                  Hidden for security. Click <span className="font-semibold">Regenerate password</span> below to issue a fresh one — it will be shown exactly once.
                </div>
              )}
            </div>
            {issuedOtp && (
              <>
                <button
                  data-testid="portal-reveal-password"
                  onClick={() => setReveal((v) => !v)}
                  className="h-8 px-3 rounded-full bg-secondary hover:bg-secondary/70 flex items-center justify-center text-[11px] uppercase tracking-widest font-semibold shrink-0"
                  title={reveal ? "Hide" : "Reveal"}
                >
                  {reveal ? "Hide" : "Reveal"}
                </button>
                <button
                  data-testid="portal-copy-password"
                  onClick={() => copy(issuedOtp, "Password")}
                  className="h-8 w-8 rounded-full bg-secondary hover:bg-secondary/70 flex items-center justify-center shrink-0"
                  title="Copy password"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>

          {/* Metadata + actions */}
          <div className="flex items-center justify-between pt-1 flex-wrap gap-3">
            <div className="text-[11px] text-muted-foreground">
              {status.last_login_at ? (
                <>Last login <span className="font-medium">{new Date(status.last_login_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</span></>
              ) : (
                <span className="italic">Never logged in yet</span>
              )}
              {status.must_change_password && <span className="ml-2 text-amber-600">· Must change password on first login</span>}
            </div>
            <div className="flex gap-2">
              {issuedOtp && (
                <Button
                  data-testid="portal-copy-all-btn"
                  onClick={copyBoth}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy all
                </Button>
              )}
              <Button
                data-testid="portal-regenerate-btn"
                disabled={busy}
                onClick={() => invite(true)}
                variant="outline"
                size="sm"
                className="rounded-full"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Regenerate
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

